// Primary 2 English Learning Data - Part 2 (Topics 11-22)
// Based on Primary English Learning P2 Topics.txt

export const p2DataPart2 = {
  topics: [
    {
      id: 'p2-topic-11',
      title: 'Countable, Uncountable Nouns',
      description: 'Learning to distinguish between countable and uncountable nouns and use them correctly',
      purpose: 'To teach students the difference between countable nouns (can be counted) and uncountable nouns (cannot be counted) and how to use them with appropriate quantifiers.',
      questions: [
        {
          question: 'Which is countable: "water" or "book"?',
          answer: 'book',
          explanation: 'Countable nouns can be counted (one book, two books).'
        },
        {
          question: 'Which is uncountable: "milk" or "apple"?',
          answer: 'milk',
          explanation: 'Uncountable nouns cannot be counted (some milk, not one milk).'
        },
        {
          question: 'I have _____ books.',
          answer: 'many',
          explanation: 'Use "many" with countable nouns.'
        },
        {
          question: 'There is _____ water in the glass.',
          answer: 'some',
          explanation: 'Use "some" with uncountable nouns in positive statements.'
        },
        {
          question: 'Which is countable: "rice" or "car"?',
          answer: 'car',
          explanation: 'Countable nouns can be counted (one car, two cars).'
        },
        {
          question: 'I don\'t have _____ money.',
          answer: 'much',
          explanation: 'Use "much" with uncountable nouns in negative statements.'
        },
        {
          question: 'There are _____ students in the class.',
          answer: 'many',
          explanation: 'Use "many" with countable nouns.'
        },
        {
          question: 'Which is uncountable: "bread" or "cat"?',
          answer: 'bread',
          explanation: 'Uncountable nouns cannot be counted (some bread, not one bread).'
        },
        {
          question: 'I have _____ time.',
          answer: 'little',
          explanation: 'Use "little" with uncountable nouns to show small quantity.'
        },
        {
          question: 'There are _____ chairs in the room.',
          answer: 'many',
          explanation: 'Use "many" with countable nouns.'
        }
      ]
    },
    {
      id: 'p2-topic-12',
      title: 'Adverbs of Frequency',
      description: 'Learning to use adverbs of frequency (always, usually, often, sometimes, rarely, never)',
      purpose: 'To teach students how to use adverbs of frequency to describe how often actions happen.',
      questions: [
        {
          question: 'I _____ go to school.',
          answer: 'always',
          explanation: 'Use "always" for actions that happen 100% of the time.'
        },
        {
          question: 'She _____ eats breakfast.',
          answer: 'usually',
          explanation: 'Use "usually" for actions that happen most of the time.'
        },
        {
          question: 'He _____ plays football.',
          answer: 'often',
          explanation: 'Use "often" for actions that happen frequently.'
        },
        {
          question: 'We _____ watch TV.',
          answer: 'sometimes',
          explanation: 'Use "sometimes" for actions that happen occasionally.'
        },
        {
          question: 'They _____ go to the cinema.',
          answer: 'rarely',
          explanation: 'Use "rarely" for actions that happen infrequently.'
        },
        {
          question: 'I _____ eat meat.',
          answer: 'never',
          explanation: 'Use "never" for actions that don\'t happen at all.'
        },
        {
          question: 'She _____ drinks coffee.',
          answer: 'usually',
          explanation: 'Use "usually" for actions that happen most of the time.'
        },
        {
          question: 'He _____ studies at night.',
          answer: 'sometimes',
          explanation: 'Use "sometimes" for actions that happen occasionally.'
        },
        {
          question: 'We _____ travel by plane.',
          answer: 'rarely',
          explanation: 'Use "rarely" for actions that happen infrequently.'
        },
        {
          question: 'They _____ miss school.',
          answer: 'never',
          explanation: 'Use "never" for actions that don\'t happen at all.'
        }
      ]
    },
    {
      id: 'p2-topic-13',
      title: 'Simple Present Tense',
      description: 'Learning to use simple present tense with more complex structures and frequency adverbs',
      purpose: 'To teach students how to form and use simple present tense with frequency adverbs and more complex sentence structures.',
      questions: [
        {
          question: 'I _____ to school every day.',
          answer: 'go',
          explanation: 'Use the base form of the verb for "I" in simple present tense.'
        },
        {
          question: 'She _____ English very well.',
          answer: 'speaks',
          explanation: 'Add "s" to the verb for third person singular (he, she, it).'
        },
        {
          question: 'They _____ football on weekends.',
          answer: 'play',
          explanation: 'Use the base form of the verb for plural subjects (we, you, they).'
        },
        {
          question: 'He _____ in Hong Kong.',
          answer: 'lives',
          explanation: 'Add "s" to the verb for third person singular (he, she, it).'
        },
        {
          question: 'We _____ dinner at 7 PM.',
          answer: 'eat',
          explanation: 'Use the base form of the verb for "we" in simple present tense.'
        },
        {
          question: 'The sun _____ in the east.',
          answer: 'rises',
          explanation: 'Add "s" to the verb for third person singular (it).'
        },
        {
          question: 'I _____ my teeth every morning.',
          answer: 'brush',
          explanation: 'Use the base form of the verb for "I" in simple present tense.'
        },
        {
          question: 'She _____ to music every day.',
          answer: 'listens',
          explanation: 'Add "s" to the verb for third person singular (she).'
        },
        {
          question: 'They _____ Chinese at home.',
          answer: 'speak',
          explanation: 'Use the base form of the verb for plural subjects (they).'
        },
        {
          question: 'He _____ basketball after school.',
          answer: 'plays',
          explanation: 'Add "s" to the verb for third person singular (he).'
        }
      ]
    },
    {
      id: 'p2-topic-14',
      title: 'Quantifiers',
      description: 'Learning to use quantifiers (some, any, many, much, a few, a little, lots of) with countable and uncountable nouns',
      purpose: 'To teach students how to use different quantifiers to express amounts and quantities with both countable and uncountable nouns.',
      questions: [
        {
          question: 'I have _____ apples.',
          answer: 'some',
          explanation: 'Use "some" for positive statements with countable nouns.'
        },
        {
          question: 'Do you have _____ water?',
          answer: 'any',
          explanation: 'Use "any" for questions and negative statements with uncountable nouns.'
        },
        {
          question: 'There are _____ books on the shelf.',
          answer: 'many',
          explanation: 'Use "many" with countable nouns to show a large quantity.'
        },
        {
          question: 'I don\'t have _____ money.',
          answer: 'much',
          explanation: 'Use "much" with uncountable nouns in negative statements.'
        },
        {
          question: 'I have _____ friends.',
          answer: 'a few',
          explanation: 'Use "a few" with countable nouns to show a small quantity.'
        },
        {
          question: 'There is _____ sugar in the bowl.',
          answer: 'some',
          explanation: 'Use "some" for positive statements with uncountable nouns.'
        },
        {
          question: 'Do you have _____ pencils?',
          answer: 'any',
          explanation: 'Use "any" for questions with countable nouns.'
        },
        {
          question: 'I have _____ time.',
          answer: 'a little',
          explanation: 'Use "a little" with uncountable nouns to show a small quantity.'
        },
        {
          question: 'There are _____ students in the class.',
          answer: 'lots of',
          explanation: 'Use "lots of" with countable nouns to show a large quantity.'
        },
        {
          question: 'I don\'t have _____ homework.',
          answer: 'much',
          explanation: 'Use "much" with uncountable nouns in negative statements.'
        }
      ]
    },
    {
      id: 'p2-topic-15',
      title: 'Punctuation + Dialogue Writing',
      description: 'Learning to use punctuation marks correctly and write simple dialogues',
      purpose: 'To teach students how to use punctuation marks correctly and write simple conversations between people.',
      questions: [
        {
          question: 'Complete: "Hello _____ how are you?"',
          answer: ',',
          explanation: 'Use a comma to separate the greeting from the question.'
        },
        {
          question: 'Fill in the blank: "I\'m fine _____ thank you."',
          answer: ',',
          explanation: 'Use a comma to separate parts of a sentence.'
        },
        {
          question: 'Complete: "What\'s your name _____"',
          answer: '?',
          explanation: 'Use a question mark to end a question.'
        },
        {
          question: 'Fill in the blank: "My name is John _____"',
          answer: '.',
          explanation: 'Use a period to end a statement.'
        },
        {
          question: 'Complete: "How old are you _____"',
          answer: '?',
          explanation: 'Use a question mark to end a question.'
        },
        {
          question: 'Fill in the blank: "I\'m seven years old _____"',
          answer: '.',
          explanation: 'Use a period to end a statement.'
        },
        {
          question: 'Complete: "Where do you live _____"',
          answer: '?',
          explanation: 'Use a question mark to end a question.'
        },
        {
          question: 'Fill in the blank: "I live in Hong Kong _____"',
          answer: '.',
          explanation: 'Use a period to end a statement.'
        },
        {
          question: 'Complete: "Do you like school _____"',
          answer: '?',
          explanation: 'Use a question mark to end a question.'
        },
        {
          question: 'Fill in the blank: "Yes _____ I do."',
          answer: ',',
          explanation: 'Use a comma to separate parts of a sentence.'
        }
      ]
    },
    {
      id: 'p2-topic-16',
      title: 'Preposition of Time',
      description: 'Learning to use prepositions of time (at, in, on, before, after, during)',
      purpose: 'To teach students how to use prepositions to describe when events happen or the timing of actions.',
      questions: [
        {
          question: 'I go to school _____ 8 o\'clock.',
          answer: 'at',
          explanation: 'Use "at" for specific times.'
        },
        {
          question: 'I was born _____ 2010.',
          answer: 'in',
          explanation: 'Use "in" for years, months, and seasons.'
        },
        {
          question: 'I have a party _____ Saturday.',
          answer: 'on',
          explanation: 'Use "on" for days of the week and dates.'
        },
        {
          question: 'I eat breakfast _____ school.',
          answer: 'before',
          explanation: 'Use "before" to show that one action happens earlier than another.'
        },
        {
          question: 'I go home _____ school.',
          answer: 'after',
          explanation: 'Use "after" to show that one action happens later than another.'
        },
        {
          question: 'I sleep _____ night.',
          answer: 'at',
          explanation: 'Use "at" for specific times like "at night".'
        },
        {
          question: 'I go on holiday _____ summer.',
          answer: 'in',
          explanation: 'Use "in" for seasons.'
        },
        {
          question: 'I have a test _____ Monday.',
          answer: 'on',
          explanation: 'Use "on" for days of the week.'
        },
        {
          question: 'I study _____ the exam.',
          answer: 'before',
          explanation: 'Use "before" to show that one action happens earlier than another.'
        },
        {
          question: 'I play games _____ homework.',
          answer: 'after',
          explanation: 'Use "after" to show that one action happens later than another.'
        }
      ]
    },
    {
      id: 'p2-topic-17',
      title: 'It\'s + Weather',
      description: 'Learning to describe weather using "It\'s" + weather adjectives',
      purpose: 'To teach students how to describe weather conditions using "It\'s" followed by weather adjectives.',
      questions: [
        {
          question: 'Complete: "It\'s _____ today." (sunny)',
          answer: 'sunny',
          explanation: 'Use "It\'s" + weather adjective to describe the weather.'
        },
        {
          question: 'Fill in the blank: "It\'s _____ outside." (rainy)',
          answer: 'rainy',
          explanation: 'Use "It\'s" + weather adjective to describe the weather.'
        },
        {
          question: 'Complete: "It\'s _____ today." (cold)',
          answer: 'cold',
          explanation: 'Use "It\'s" + weather adjective to describe the weather.'
        },
        {
          question: 'Fill in the blank: "It\'s _____ today." (hot)',
          answer: 'hot',
          explanation: 'Use "It\'s" + weather adjective to describe the weather.'
        },
        {
          question: 'Complete: "It\'s _____ today." (windy)',
          answer: 'windy',
          explanation: 'Use "It\'s" + weather adjective to describe the weather.'
        },
        {
          question: 'Fill in the blank: "It\'s _____ today." (cloudy)',
          answer: 'cloudy',
          explanation: 'Use "It\'s" + weather adjective to describe the weather.'
        },
        {
          question: 'Complete: "It\'s _____ today." (warm)',
          answer: 'warm',
          explanation: 'Use "It\'s" + weather adjective to describe the weather.'
        },
        {
          question: 'Fill in the blank: "It\'s _____ today." (foggy)',
          answer: 'foggy',
          explanation: 'Use "It\'s" + weather adjective to describe the weather.'
        },
        {
          question: 'Complete: "It\'s _____ today." (snowy)',
          answer: 'snowy',
          explanation: 'Use "It\'s" + weather adjective to describe the weather.'
        },
        {
          question: 'Fill in the blank: "It\'s _____ today." (stormy)',
          answer: 'stormy',
          explanation: 'Use "It\'s" + weather adjective to describe the weather.'
        }
      ]
    },
    {
      id: 'p2-topic-18',
      title: 'Sentence Structure',
      description: 'Learning to construct different types of sentences (statements, questions, commands)',
      purpose: 'To teach students how to construct different types of sentences with proper word order and structure.',
      questions: [
        {
          question: 'Complete: "I _____ a student."',
          answer: 'am',
          explanation: 'Use the correct form of "to be" for the subject "I".'
        },
        {
          question: 'Fill in the blank: "She _____ English."',
          answer: 'speaks',
          explanation: 'Use the correct verb form for third person singular.'
        },
        {
          question: 'Complete: "_____ you like ice cream?"',
          answer: 'Do',
          explanation: 'Use "Do" to form questions with "you".'
        },
        {
          question: 'Fill in the blank: "_____ is your name?"',
          answer: 'What',
          explanation: 'Use "What" to ask about things or names.'
        },
        {
          question: 'Complete: "_____ the door."',
          answer: 'Open',
          explanation: 'Use the base form of the verb for imperatives.'
        },
        {
          question: 'Fill in the blank: "They _____ football."',
          answer: 'play',
          explanation: 'Use the base form of the verb for plural subjects.'
        },
        {
          question: 'Complete: "_____ do you live?"',
          answer: 'Where',
          explanation: 'Use "Where" to ask about location.'
        },
        {
          question: 'Fill in the blank: "He _____ in Hong Kong."',
          answer: 'lives',
          explanation: 'Use the correct verb form for third person singular.'
        },
        {
          question: 'Complete: "_____ your homework."',
          answer: 'Do',
          explanation: 'Use the base form of the verb for imperatives.'
        },
        {
          question: 'Fill in the blank: "We _____ to school."',
          answer: 'go',
          explanation: 'Use the base form of the verb for "we".'
        }
      ]
    },
    {
      id: 'p2-topic-19',
      title: 'Telling the Time',
      description: 'Learning to tell time using o\'clock, half past, quarter past, quarter to',
      purpose: 'To teach students how to read and tell time using different time expressions.',
      questions: [
        {
          question: 'What time is it when the clock shows 3:00?',
          answer: 'three o\'clock',
          explanation: 'Use "o\'clock" for exact hours.'
        },
        {
          question: 'What time is it when the clock shows 2:30?',
          answer: 'half past two',
          explanation: 'Use "half past" for 30 minutes past the hour.'
        },
        {
          question: 'What time is it when the clock shows 1:15?',
          answer: 'quarter past one',
          explanation: 'Use "quarter past" for 15 minutes past the hour.'
        },
        {
          question: 'What time is it when the clock shows 4:45?',
          answer: 'quarter to five',
          explanation: 'Use "quarter to" for 15 minutes before the next hour.'
        },
        {
          question: 'What time is it when the clock shows 6:00?',
          answer: 'six o\'clock',
          explanation: 'Use "o\'clock" for exact hours.'
        },
        {
          question: 'What time is it when the clock shows 5:30?',
          answer: 'half past five',
          explanation: 'Use "half past" for 30 minutes past the hour.'
        },
        {
          question: 'What time is it when the clock shows 7:15?',
          answer: 'quarter past seven',
          explanation: 'Use "quarter past" for 15 minutes past the hour.'
        },
        {
          question: 'What time is it when the clock shows 8:45?',
          answer: 'quarter to nine',
          explanation: 'Use "quarter to" for 15 minutes before the next hour.'
        },
        {
          question: 'What time is it when the clock shows 9:00?',
          answer: 'nine o\'clock',
          explanation: 'Use "o\'clock" for exact hours.'
        },
        {
          question: 'What time is it when the clock shows 10:30?',
          answer: 'half past ten',
          explanation: 'Use "half past" for 30 minutes past the hour.'
        }
      ]
    },
    {
      id: 'p2-topic-20',
      title: 'Object Pronouns',
      description: 'Learning to use object pronouns (me, you, him, her, it, us, them)',
      purpose: 'To teach students how to use object pronouns to replace nouns that receive the action of the verb.',
      questions: [
        {
          question: 'The teacher helps _____.',
          answer: 'me',
          explanation: 'Use "me" as the object pronoun for "I".'
        },
        {
          question: 'I like _____.',
          answer: 'you',
          explanation: 'Use "you" as the object pronoun for "you".'
        },
        {
          question: 'She loves _____.',
          answer: 'him',
          explanation: 'Use "him" as the object pronoun for "he".'
        },
        {
          question: 'He helps _____.',
          answer: 'her',
          explanation: 'Use "her" as the object pronoun for "she".'
        },
        {
          question: 'I see _____.',
          answer: 'it',
          explanation: 'Use "it" as the object pronoun for "it".'
        },
        {
          question: 'The teacher teaches _____.',
          answer: 'us',
          explanation: 'Use "us" as the object pronoun for "we".'
        },
        {
          question: 'I know _____.',
          answer: 'them',
          explanation: 'Use "them" as the object pronoun for "they".'
        },
        {
          question: 'She calls _____.',
          answer: 'me',
          explanation: 'Use "me" as the object pronoun for "I".'
        },
        {
          question: 'He likes _____.',
          answer: 'her',
          explanation: 'Use "her" as the object pronoun for "she".'
        },
        {
          question: 'We help _____.',
          answer: 'them',
          explanation: 'Use "them" as the object pronoun for "they".'
        }
      ]
    },
    {
      id: 'p2-topic-21',
      title: 'Question Words',
      description: 'Learning to use question words (what, where, when, who, why, how) in more complex sentences',
      purpose: 'To teach students how to form questions using different question words to gather specific information in more complex contexts.',
      questions: [
        {
          question: '_____ is your favorite color?',
          answer: 'What',
          explanation: 'Use "What" to ask about things, names, or objects.'
        },
        {
          question: '_____ do you live?',
          answer: 'Where',
          explanation: 'Use "Where" to ask about location or place.'
        },
        {
          question: '_____ do you go to school?',
          answer: 'When',
          explanation: 'Use "When" to ask about time or schedule.'
        },
        {
          question: '_____ is your best friend?',
          answer: 'Who',
          explanation: 'Use "Who" to ask about people.'
        },
        {
          question: '_____ do you like ice cream?',
          answer: 'Why',
          explanation: 'Use "Why" to ask for reasons or explanations.'
        },
        {
          question: '_____ old are you?',
          answer: 'How',
          explanation: 'Use "How" to ask about quantity, age, or manner.'
        },
        {
          question: '_____ is your school?',
          answer: 'Where',
          explanation: 'Use "Where" to ask about location.'
        },
        {
          question: '_____ do you eat breakfast?',
          answer: 'When',
          explanation: 'Use "When" to ask about time.'
        },
        {
          question: '_____ is your favorite subject?',
          answer: 'What',
          explanation: 'Use "What" to ask about preferences or choices.'
        },
        {
          question: '_____ do you go to school?',
          answer: 'How',
          explanation: 'Use "How" to ask about the way or method.'
        }
      ]
    },
    {
      id: 'p2-topic-22',
      title: 'Possessive Adjectives',
      description: 'Learning to use possessive adjectives (my, your, his, her, its, our, their) in more complex contexts',
      purpose: 'To teach students how to use possessive adjectives to show ownership or relationship between people and things in more complex sentences.',
      questions: [
        {
          question: 'This is _____ book.',
          answer: 'my',
          explanation: 'Use "my" to show that the book belongs to the speaker.'
        },
        {
          question: 'What is _____ name?',
          answer: 'your',
          explanation: 'Use "your" to ask about the listener\'s name.'
        },
        {
          question: 'This is _____ car.',
          answer: 'his',
          explanation: 'Use "his" to show that the car belongs to a male person.'
        },
        {
          question: 'That is _____ bag.',
          answer: 'her',
          explanation: 'Use "her" to show that the bag belongs to a female person.'
        },
        {
          question: 'The cat is playing with _____ toy.',
          answer: 'its',
          explanation: 'Use "its" to show that the toy belongs to the cat (animal).'
        },
        {
          question: 'This is _____ house.',
          answer: 'our',
          explanation: 'Use "our" to show that the house belongs to the speaker and others.'
        },
        {
          question: 'Those are _____ books.',
          answer: 'their',
          explanation: 'Use "their" to show that the books belong to other people.'
        },
        {
          question: 'Is this _____ pencil?',
          answer: 'your',
          explanation: 'Use "your" to ask if the pencil belongs to the listener.'
        },
        {
          question: 'This is _____ dog.',
          answer: 'my',
          explanation: 'Use "my" to show that the dog belongs to the speaker.'
        },
        {
          question: 'That is _____ school.',
          answer: 'our',
          explanation: 'Use "our" to show that the school belongs to the speaker and others.'
        }
      ]
    }
  ]
}; 